import { ColumnLayout, Header, Container, SpaceBetween, Box } from '@amzn/awsui-components-console';
import { get as _get } from 'lodash';
import { useIntl } from 'react-intl';

import type { ActivityNote, Maybe } from '@/api/generated';
import { useGetLearningActivity } from '@/api/hooks';
import { Loader } from '@/components';

import messages from './index.messages';

const ClassNotesPage = () => {
    const { formatMessage } = useIntl();
    const { data: learningActivityQuery, isLoading } = useGetLearningActivity();
    const showIsLoading = isLoading || !learningActivityQuery?.getLearningActivity;
    const learningActivity = learningActivityQuery?.getLearningActivity || {};

    // Transform learning activity data into organized format
    const customerData = reduceCustomerTrainingBrief(learningActivity);

    return (
        <Loader isLoading={showIsLoading}>
            <SpaceBetween direction='vertical' size='l'>
                <Container
                    header={
                        <Header variant='h2'>
                            {formatMessage(messages.trainingBriefingHeader)}
                        </Header>
                    }
                >
                    <ColumnLayout columns={4} borders='vertical' variant='text-grid'>
                        <div>
                            <Box variant='awsui-key-label'>
                                {formatMessage(messages.whatTheCustomerDoes)}
                            </Box>
                            <Box>
                                {customerData.what_the_customer_does.map((item: string) => (
                                    <div>{item}</div>
                                ))}
                            </Box>
                        </div>
                        <div>
                            <Box variant='awsui-key-label'>
                                {formatMessage(messages.whereAreTheyInTheirCloudJourney)}
                            </Box>
                            <Box>
                                {customerData.where_are_they_in_their_cloud_journey.map(
                                    (item: string) => (
                                        <div>{item}</div>
                                    ),
                                )}
                            </Box>
                        </div>
                        <div>
                            <Box variant='awsui-key-label'>
                                {formatMessage(messages.motivationsForUsingAws)}
                            </Box>
                            <Box>
                                {customerData.motivations_for_using_aws.map((item: string) => (
                                    <div>{item}</div>
                                ))}
                            </Box>
                        </div>
                        <div>
                            <Box variant='awsui-key-label'>
                                {formatMessage(messages.specialNeedsForTrainingDelivery)}
                            </Box>
                            <Box>
                                {customerData.special_needs_for_training_delivery.map(
                                    (item: string) => (
                                        <div>{item}</div>
                                    ),
                                )}
                            </Box>
                        </div>
                    </ColumnLayout>
                </Container>
                <Container
                    header={
                        <Header variant='h2'>
                            {formatMessage(messages.instructorNotesHeader)}
                        </Header>
                    }
                >
                    <ColumnLayout columns={1} borders='vertical' variant='text-grid'>
                        <div>
                            {_get(learningActivity, 'instructor_notes', [])?.map(
                                (item: ActivityNote) => (
                                    <div>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                            {item.description}
                                        </div>
                                        <Box variant='awsui-key-label'>
                                            {formatMessage(messages.notesEditedHeader)}
                                        </Box>
                                        {getInstructorNotesEditedBox(item, formatMessage)}
                                        <br />
                                    </div>
                                ),
                            )}
                        </div>
                    </ColumnLayout>
                </Container>
            </SpaceBetween>
        </Loader>
    );
};

const getInstructorNotesEditedBox = (activityNote: ActivityNote, formatMessage: any): any => {
    if (!activityNote.timestamp && !activityNote.updated_by) return '';

    let boxContent;
    if (activityNote.updated_by) {
        boxContent = formatMessage(messages.editedByText, {
            formattedDateTime: formatTimestamp(activityNote.timestamp),
            author: activityNote.updated_by,
        });
    } else {
        boxContent = formatTimestamp(activityNote.timestamp);
    }
    return <Box>{boxContent}</Box>;
};

// Format timestamp to "Month DD, YYYY MM:SS<am/pm>" format
const formatTimestamp = (timestamp: Maybe<number>) => {
    if (!timestamp) return '';

    // Convert Unix timestamp (seconds) to milliseconds for JavaScript Date
    const date = new Date(timestamp * 1000);
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    return date.toLocaleString('en-US', options).replace(/\n/g, ' ').replace(' at ', ' ');
};

const reduceCustomerTrainingBrief = (learningActivity: any) => {
    const customerData: any = {
        what_the_customer_does: [],
        where_are_they_in_their_cloud_journey: [],
        motivations_for_using_aws: [],
        special_needs_for_training_delivery: [],
    };

    // Populate arrays with customer data
    _get(learningActivity, 'customers', [])?.forEach((customer: any) => {
        if (customer.training_briefing?.what_the_customer_does) {
            customerData.what_the_customer_does.push(
                customer.training_briefing.what_the_customer_does,
            );
        }
        if (customer.training_briefing?.where_are_they_in_their_cloud_journey) {
            customerData.where_are_they_in_their_cloud_journey.push(
                customer.training_briefing.where_are_they_in_their_cloud_journey,
            );
        }
        if (customer.training_briefing?.motivations_for_using_aws) {
            customerData.motivations_for_using_aws.push(
                customer.training_briefing.motivations_for_using_aws,
            );
        }
        if (customer.training_briefing?.special_needs_for_training_delivery) {
            customerData.special_needs_for_training_delivery.push(
                customer.training_briefing.special_needs_for_training_delivery,
            );
        }
    });
    return customerData;
};

export default ClassNotesPage;
